import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { ApiService } from './api.service';
import { RuntimeConfigService } from './runtime-config.service';

let refreshInFlight: Promise<void> | null = null;
let authExpiredDispatched = false;

export function resetAuthRefreshStateForTests(): void {
  refreshInFlight = null;
  authExpiredDispatched = false;
}

export const authRefreshInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const api = inject(ApiService);
  const config = inject(RuntimeConfigService);

  return next(request).pipe(
    catchError(error => {
      if (!shouldRefreshAndRetry(request, error, api, config)) {
        return throwError(() => error);
      }

      refreshInFlight ??= api.refreshSession().finally(() => {
        refreshInFlight = null;
      });

      return from(refreshInFlight).pipe(
        switchMap(() => {
          authExpiredDispatched = false;
          return next(withCurrentAuthorization(request, api));
        }),
        catchError(refreshError => {
          dispatchAuthExpiredOnce();
          return throwError(() => refreshError);
        })
      );
    })
  );
};

function shouldRefreshAndRetry(request: HttpRequest<unknown>, error: unknown, api: ApiService, config: RuntimeConfigService): boolean {
  const apiBaseUrl = config.apiBaseUrl || '/api';
  return error instanceof HttpErrorResponse
    && (error.status === 401 || error.status === 403)
    && request.url.startsWith(apiBaseUrl)
    && !request.headers.has('X-PhDCompass-Retry')
    && api.canRefreshSession;
}

function withCurrentAuthorization(request: HttpRequest<unknown>, api: ApiService): HttpRequest<unknown> {
  return request.clone({
    setHeaders: {
      Authorization: api.authorizationHeader,
      'X-PhDCompass-Retry': '1'
    }
  });
}

function dispatchAuthExpiredOnce(): void {
  if (authExpiredDispatched) return;
  authExpiredDispatched = true;
  window.dispatchEvent(new CustomEvent('phdcompass-auth-expired'));
}